import React, { Component, Fragment } from 'react';
import cn from 'classnames';
import moment from 'moment';
import { injectIntl } from 'react-intl';

import 'moment/locale/pt.js';
import 'moment/locale/en-ca.js';

import { ReactComponent as LeftArrow } from '../../assets/icons/ico_arrow_slide_left.svg';
import { ReactComponent as RightArrow } from '../../assets/icons/ico_arrow_slide_right.svg';

import TryAgain from '../try-again/try-again.component.js';

import './calendar.component.scss';

const LoaderCalendar = () => (
	<div className="calendar">
		{Array(5)
			.fill(0)
			.map(() => (
				<div className="calendar-week calendar-week-loading">
					<span className="calendar-day-of-week day-loading day-1" />
					<span className="calendar-day-of-week day-loading day-2" />
					<span className="calendar-day-of-week day-loading day-3" />
					<span className="calendar-day-of-week day-loading day-4" />
					<span className="calendar-day-of-week day-loading day-5" />
					<span className="calendar-day-of-week day-loading day-6" />
					<span className="calendar-day-of-week day-loading day-7" />
				</div>
			))}
	</div>
);


const DaysInner = ({ intl: { formatMessage } }) => {
	const days = [
		formatMessage({ id: 'day.mon' }),
		formatMessage({ id: 'day.tue' }),
		formatMessage({ id: 'day.wed' }),
		formatMessage({ id: 'day.thu' }),
		formatMessage({ id: 'day.fri' }),
		formatMessage({ id: 'day.sat' }),
		formatMessage({ id: 'day.sun' }),
	];

	return (
		<div className="days">
			{days.map(day => (
				<div key={day} className="day">{day}</div>
			))}
		</div>
	);
};
const Days = injectIntl(DaysInner);

const DayOfWeek = ({
	date, handleDayClick, selectedDate, available, hidden,
}) => (
	<span
		className={cn(
			'calendar-day-of-week',
			{ hidden },
			{ available },
			{ selected: selectedDate && selectedDate.isSame(date, 'day') },
		)}
		onClick={available ? () => handleDayClick(date) : null}
	>
		{date.format('DD')}
	</span>
);

function isAvailable(schedule, dayOfMonth) {
	return schedule.find(day => moment(day.day).format('DD/MM/YYYY') === moment(dayOfMonth).format('DD/MM/YYYY'));
}

const Week = ({
	weekStartDate, handleDayClick, selectedDate, schedule, actualMonth,
}) => {
	const weekDayItems = [];
	let dayOfMonth = null;
	for (
		let dayOfWeekIterator = 0;
		dayOfWeekIterator < 7;
		dayOfWeekIterator += 1
	) {
		dayOfMonth = moment(weekStartDate).add(dayOfWeekIterator, 'd');

		weekDayItems.push(
			<DayOfWeek
				key={`${weekStartDate.format('DD MM YY')}-${dayOfWeekIterator}`}
				date={dayOfMonth}
				handleDayClick={handleDayClick}
				selectedDate={selectedDate}
				available={isAvailable(schedule, dayOfMonth)}
				hidden={actualMonth + 1 !== parseInt(dayOfMonth.format('MM'))}
			/>,
		);
	}

	return <div className="calendar-week">{weekDayItems}</div>;
};

class Calendar extends Component {
	state = {
		date: moment(),
	};

	componentDidMount() {
		const { selectedDate } = this.props;

		if (selectedDate) {
			this.setState({ date: moment(selectedDate) });
		}
	}

	handleDayClick = (date) => {
		const { onDayClick } = this.props;

		if (onDayClick) onDayClick(date);
	};

	previousMonth = () => {
		const { date } = this.state;
		const { handleMonth } = this.props;

		handleMonth(moment(date).subtract(1, 'month'));
		this.setState(prevState => ({
			date: moment(prevState.date).subtract(1, 'month'),
		}));
	};

	nextMonth = () => {
		const { handleMonth } = this.props;
		const { date } = this.state;

		handleMonth(moment(date).add(1, 'month'));
		this.setState(prevState => ({
			date: moment(prevState.date).add(1, 'month'),
		}));
	};

	renderWeeks = () => {
		const { date } = this.state;
		const { selectedDate, schedule } = this.props;

		let startDate = moment(date)
			.utc(false)
			.startOf('month')
			.startOf('isoWeek');

		const monthStartDate = moment(date)
			.utc(false)
			.startOf('month');

		if (monthStartDate.isBefore(startDate)) {
			startDate = moment(date)
				.utc(false)
				.startOf('month')
				.startOf('isoWeek');
		}

		const endDate = moment(date)
			.utc(false)
			.endOf('month')
			.endOf('isoWeek');

		const amountOfWeeks = moment(endDate - startDate).weeks() - 1;

		const weeks = Math.min(
			moment.duration(endDate.diff(startDate)).as('WEEKS'),
			amountOfWeeks,
		);

		const weeksItems = [];

		for (let weekIterator = 0; weekIterator < weeks; weekIterator += 1) {
			weeksItems.push(
				<Week
					key={`week-${weekIterator}`}
					handleDayClick={this.handleDayClick}
					weekStartDate={moment(startDate).add(weekIterator, 'w')}
					selectedDate={selectedDate}
					schedule={schedule}
					actualMonth={date.format('MM') - 1}
				/>,
			);
		}

		return weeksItems;
	};

	render() {
		const { date } = this.state;
		const {
			isLoading, intl: { locale }, setInterval, schedule,
		} = this.props;

		moment.locale(locale);

		const actualDate = moment().utc(false);

		return (
			<div className="calendar">

				<div className="row">
					<div className="col-xs-10 col-sm-6">
						<span className="calendar-title">
							{(!isLoading && schedule)
								? moment(date).utc(false).format('MMMM YYYY')
								: ''
							}
						</span>
					</div>

					<div className="col-xs-2 col-sm-6 end-xs">
						{(!isLoading && schedule) && (
							<Fragment>
								<span
									className={cn('calendar-navigation-icon previous', {
										disabled: date.isSameOrBefore(actualDate),
									})}
									onClick={this.previousMonth}
								>
									{LeftArrow && <LeftArrow />}
								</span>
								<span
									className="calendar-navigation-icon next"
									onClick={this.nextMonth}
								>
									{RightArrow && <RightArrow />}
								</span>
							</Fragment>
						)}
					</div>
				</div>

				{schedule && <Days />}

				{isLoading && <LoaderCalendar /> }

				{(!isLoading && schedule) && this.renderWeeks()}

				{(!schedule && !isLoading) && <TryAgain handleFunction={setInterval} />}

			</div>
		);
	}
}

export default injectIntl(Calendar);
